import { useEffect, useRef, useState } from 'react';
import type { PathDotSettings } from './flow-chart-paths';

const IS_TEST = import.meta.env.MODE === 'test';

function getRandomIntBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

type DotProps = {
  dot: PathDotSettings;
};

/** A single dot that repeatedly rides an enabled flow path with randomized timing. */
export function Dot({ dot }: DotProps) {
  const [duration, setDuration] = useState(() => getRandomIntBetween(2400, 5600));
  const animateRef = useRef<SVGAnimateMotionElement>(null);

  useEffect(() => {
    const element = animateRef.current;
    if (element == null) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      setDuration(getRandomIntBetween(2400, 5600));
      if (IS_TEST) {
        element.beginElement();
        return;
      }
      timeoutId = setTimeout(() => element.beginElement(), getRandomIntBetween(0, 4000));
    };

    const onAnimationEnd = () => schedule();

    if (!IS_TEST) {
      element.addEventListener('endEvent', onAnimationEnd);
    }
    schedule();

    return () => {
      if (!IS_TEST) {
        element.removeEventListener('endEvent', onAnimationEnd);
      }
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <circle r="4" fill={dot.color} visibility="hidden">
      <animateMotion
        ref={animateRef}
        id={dot.id}
        dur={`${duration}ms`}
        begin="indefinite"
        calcMode="spline"
        keyPoints="0;1"
        keyTimes="0;1"
        keySplines="0.42 0 0.58 1"
      >
        <mpath xlinkHref={`#${dot.pathId}`} />
      </animateMotion>
      <set attributeName="visibility" from="hidden" to="visible" begin={`${dot.id}.begin`} />
      <set attributeName="visibility" from="visible" to="hidden" begin={`${dot.id}.end`} />
    </circle>
  );
}
